package com.schoolconnect.app.feature.timetable

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.material3.VerticalDivider
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.unit.Dp
import com.schoolconnect.app.designsystem.AppBackground
import com.schoolconnect.app.designsystem.AppPageHeader
import com.schoolconnect.app.designsystem.AppPageSubtitle
import com.schoolconnect.app.designsystem.AppBorder
import com.schoolconnect.app.designsystem.AppCard
import com.schoolconnect.app.designsystem.AppDropdownMenu
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.feature.common.ShellPageHeader
import com.schoolconnect.app.designsystem.list.AdminListHorizontalScrollbar
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.chevron_down
import org.jetbrains.compose.resources.painterResource

private val TeacherTimetableGrid = Color(0xFF9CC5FF)
private val TeacherTimetableHeader = Color(0xFFE8F2FF)
private val TeacherTimetableHoliday = Color(0xFFE7EEF7)
@Composable
internal fun TeacherTimeTableScreen(
    state: TeacherTimeTableUiState,
    onEvent: (TeacherTimeTableUiEvent) -> Unit,
    headerActions: @Composable RowScope.() -> Unit,
    contentHorizontalPadding: Dp = 24.dp,
    contentVerticalPadding: Dp = 16.dp,
    modifier: Modifier = Modifier
) {
    var classMenuExpanded by remember { mutableStateOf(false) }

    Column(
        modifier.fillMaxSize()
            .background(AppBackground)
            .padding(horizontal = contentHorizontalPadding, vertical = contentVerticalPadding),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        ShellPageHeader(
            title = "Time Table",
            subtitle = "View published timetables for your classes.",
            titleColor = AppPageHeader,
            subtitleColor = AppPageSubtitle,
            headerActions = headerActions
        )

        AppCard(Modifier.fillMaxWidth(), containerColor = Color.White) {
            BoxWithConstraints(Modifier.fillMaxWidth().padding(horizontal = 18.dp, vertical = 16.dp)) {
                val compact = maxWidth < 850.dp
                if (compact) {
                    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                        ClassSelector(state, classMenuExpanded, { classMenuExpanded = it }, onEvent)
                        Row(horizontalArrangement = Arrangement.spacedBy(18.dp), verticalAlignment = Alignment.CenterVertically) {
                            ScheduleMetadata("Special Schedule Day:", state.specialScheduleLabel, Modifier.weight(1f))
                            ScheduleMetadata("Weekly Off Day:", state.weeklyOffLabel, Modifier.weight(1f))
                        }
                        Text(state.lastUpdatedLabel?.let { "Timetable: $it" } ?: "", color = Color(0xFF5365C8), fontSize = 13.sp)
                    }
                } else {
                    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                        ClassSelector(state, classMenuExpanded, { classMenuExpanded = it }, onEvent)
                        VerticalDivider(Modifier.padding(horizontal = 24.dp).height(54.dp), color = TeacherTimetableGrid)
                        ScheduleMetadata("Special Schedule Day:", state.specialScheduleLabel, Modifier.weight(.9f))
                        VerticalDivider(Modifier.padding(horizontal = 24.dp).height(54.dp), color = TeacherTimetableGrid)
                        ScheduleMetadata("Weekly Off Day:", state.weeklyOffLabel, Modifier.weight(1.15f))
                        Text(state.lastUpdatedLabel?.let { "Timetable: $it" } ?: "", color = Color(0xFF5365C8), fontSize = 13.sp, modifier = Modifier.padding(start = 20.dp))
                    }
                }
            }
        }

        state.weekLabel?.let { Text("Week of $it", color = Color(0xFF6B7280), fontSize = 13.sp) }

        when {
            state.isLoadingClasses || state.isLoadingSchedule -> Box(Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) { CircularProgressIndicator(color = AppPrimary) }
            state.errorMessage != null -> AppCard(Modifier.fillMaxWidth().weight(1f), containerColor = Color.White) {
                Column(Modifier.fillMaxWidth().padding(24.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text(state.errorMessage, color = Color(0xFFDC2626), fontSize = 14.sp)
                    Text("Retry", color = AppPrimary, fontSize = 14.sp, modifier = Modifier.clickable { onEvent(TeacherTimeTableUiEvent.Retry) }.padding(4.dp))
                }
            }
            state.classes.isEmpty() -> AppCard(Modifier.fillMaxWidth().weight(1f), containerColor = Color.White) {
                Text("No assigned classes are available for this academic year.", color = Color(0xFF6B7280), fontSize = 14.sp, modifier = Modifier.padding(24.dp))
            }
            state.hasPublishedTimetable == false -> AppCard(Modifier.fillMaxWidth().weight(1f), containerColor = Color.White) {
                Text("No published timetable is available for this class.", color = Color(0xFF6B7280), fontSize = 14.sp, modifier = Modifier.padding(24.dp))
            }
            else -> AppCard(Modifier.fillMaxWidth().weight(1f), containerColor = Color.White) {
            Column(
                Modifier.fillMaxSize().padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(18.dp)
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    Text("Weekly Schedule", color = AppNavy, fontSize = 20.sp, fontWeight = FontWeight.SemiBold)
                    if (state.weekly.rows.isNotEmpty()) TeacherScheduleGrid(state.weekly.times, state.weekly.rows)
                    else Text("No regular schedule for this week.", color = Color(0xFF6B7280), fontSize = 14.sp)
                }
                if (state.special.rows.isNotEmpty()) Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    val specialDay = state.specialScheduleLabel.takeIf { it != "—" && !it.contains(',') }
                    Text(specialDay?.let { "$it Schedule" } ?: "Special Schedule", color = AppNavy, fontSize = 20.sp, fontWeight = FontWeight.SemiBold)
                    TeacherScheduleGrid(state.special.times, state.special.rows)
                }
                Spacer(Modifier.height(1.dp))
            }
        }
        }
    }
}

@Composable
private fun ClassSelector(
    state: TeacherTimeTableUiState,
    expanded: Boolean,
    onExpandedChange: (Boolean) -> Unit,
    onEvent: (TeacherTimeTableUiEvent) -> Unit
) {
    Column(Modifier.widthIn(max = 360.dp).fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(7.dp)) {
        Text("Class / Section", color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
        Box(
            Modifier.fillMaxWidth().height(40.dp)
                .border(1.dp, Color(0xFFD5DDF7), RoundedCornerShape(6.dp))
                .clickable { onExpandedChange(true) }
                .padding(horizontal = 12.dp),
            contentAlignment = Alignment.CenterStart
        ) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text(state.selectedClass?.label ?: if (state.isLoadingClasses) "Loading classes…" else "Select a class", color = AppNavy, fontSize = 14.sp)
                Icon(painterResource(Res.drawable.chevron_down), contentDescription = "Select class or section", tint = AppNavy, modifier = Modifier.size(18.dp))
            }
            AppDropdownMenu(expanded, { onExpandedChange(false) }, containerColor = Color.White) {
                state.classes.forEach { classOption ->
                    DropdownMenuItem(text = { Text(classOption.label, color = AppNavy) }, onClick = { onEvent(TeacherTimeTableUiEvent.ClassSelected(classOption.id)); onExpandedChange(false) })
                }
            }
        }
    }
}

@Composable
private fun ScheduleMetadata(label: String, value: String, modifier: Modifier = Modifier) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(label, color = Color(0xFF5365C8), fontSize = 14.sp)
        Text(value, color = AppNavy, fontSize = 16.sp, fontWeight = FontWeight.Medium)
    }
}

@Composable
private fun TeacherScheduleGrid(times: List<String>, rows: List<ParentTimeTableScheduleRow>) {
    val horizontalScroll = rememberScrollState()
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        val dayColumnWidth = 92.dp
        val slotCount = maxOf(5, times.size)
        val periodColumnWidth = maxOf((maxWidth - dayColumnWidth) / slotCount, 150.dp)
        val tableWidth = dayColumnWidth + periodColumnWidth * slotCount
        Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Box(Modifier.fillMaxWidth().border(1.dp, TeacherTimetableGrid, RoundedCornerShape(6.dp)).horizontalScroll(horizontalScroll)) {
              Column(Modifier.width(tableWidth)) {
                Row(Modifier.width(tableWidth)) {
                    TeacherScheduleCell("Day", dayColumnWidth, header = true, centered = true)
                    repeat(slotCount) { index ->
                        TeacherScheduleCell(times.getOrElse(index) { "" }, periodColumnWidth, header = true, centered = true)
                    }
                }
                rows.forEach { row ->
                    Row(Modifier.width(tableWidth)) {
                        TeacherScheduleCell(row.day, dayColumnWidth, centered = true, holiday = row.holiday != null)
                        if (row.holiday != null) {
                            Box(
                                Modifier.width(periodColumnWidth * slotCount).height(44.dp)
                                    .background(TeacherTimetableHoliday)
                                    .border(.5.dp, TeacherTimetableGrid),
                                contentAlignment = Alignment.Center
                            ) { Text(row.holiday, color = AppNavy, fontSize = 13.sp) }
                        } else {
                            repeat(slotCount) { index ->
                                TeacherScheduleCell(row.subjects.getOrNull(index).orEmpty(), periodColumnWidth)
                            }
                        }
                    }
                }
              }
            }
            AdminListHorizontalScrollbar(state = horizontalScroll, modifier = Modifier.fillMaxWidth())
        }
    }
}

@Composable
private fun TeacherScheduleCell(
    value: String,
    width: androidx.compose.ui.unit.Dp,
    header: Boolean = false,
    centered: Boolean = false,
    holiday: Boolean = false
) {
    Row(
        Modifier.width(width).height(if (header) 44.dp else 44.dp)
            .background(when { header -> TeacherTimetableHeader; holiday -> TeacherTimetableHoliday; else -> Color.White })
            .border(.5.dp, TeacherTimetableGrid)
            .padding(horizontal = if (centered) 8.dp else 12.dp, vertical = 6.dp),
        horizontalArrangement = if (centered) Arrangement.Center else Arrangement.Start,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(value, color = if (header) Color(0xFF1635E8) else AppNavy, fontSize = 13.sp, fontWeight = if (header) FontWeight.Medium else FontWeight.Normal, maxLines = 2)
    }
}
